/**
 * Teacher Assessment Page
 *
 * One-question-at-a-time interface with:
 * - Auto-advance after selecting answer
 * - Progress bar
 * - Auto-save every 500ms to localStorage
 * - Background sync to database every 5 minutes
 * - Milestone celebrations at 25%, 50%, 75%
 * - Clean, professional UI with custom dialogs
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useAutoSave, useLoadSavedData, useBackgroundSync } from '../hooks';

// Professional Dialog Component
const Dialog = ({ isOpen, onClose, type = 'info', title, message, confirmText, onConfirm }) => {
  if (!isOpen) return null;

  const icons = {
    success: (
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ),
    error: (
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    ),
    warning: (
      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    ),
    info: (
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
  };

  const buttonColors = {
    success: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700',
    error: 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
    warning: 'bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700',
    info: 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform animate-bounce-in">
        {icons[type]}

        <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
          {title}
        </h3>
        <p className="text-gray-600 text-center mb-6">
          {message}
        </p>

        <div className="flex gap-3">
          {onConfirm && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
            >
              Batal
            </button>
          )}
          <button
            onClick={onConfirm || onClose}
            className={`flex-1 px-4 py-3 text-white rounded-xl font-medium transition-all ${buttonColors[type]}`}
          >
            {confirmText || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TeacherAssessment = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();

  // State
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'OK',
    onConfirm: null,
  });

  // Storage key for this assessment
  const storageKey = `teacher_assessment_${profile?.id}`;

  // Track if we're advancing to prevent double advance
  const isAdvancingRef = useRef(false);
  // Track which milestones have been shown to prevent duplicates
  const milestoneShownRef = useRef({});

  // Load saved data from localStorage
  const { savedData, clearSavedData } = useLoadSavedData(storageKey);

  // Auto-save to localStorage (500ms debounce)
  const { isSaving, lastSaved } = useAutoSave(storageKey, {
    responses,
    currentIndex,
    sessionId,
  }, 500);

  // Background sync to database (5 minutes)
  const { isSyncing } = useBackgroundSync(
    storageKey,
    async (data) => {
      if (sessionId) {
        await assessmentService.autoSave(sessionId, data);
      }
    },
    300000
  );

  // Load questions on mount
  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore saved progress
  useEffect(() => {
    if (savedData && savedData.responses) {
      setResponses(savedData.responses);
      setCurrentIndex(savedData.currentIndex || 0);
      setSessionId(savedData.sessionId);
    }
  }, [savedData]);

  const showDialog = (type, title, message, confirmText, onConfirm) => {
    setDialog({
      isOpen: true,
      type,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const loadQuestions = async () => {
    try {
      const { questions: data, error } = await assessmentService.getQuestions('teacher');

      if (error) throw error;

      setQuestions(data);

      // Create session if not exists
      if (!sessionId && !savedData?.sessionId) {
        const { session, error: sessionError } = await assessmentService.createSession({
          user_id: profile?.id,
          total_questions: data.length,
          is_anonymous: false,
          target_school_id: profile?.school_id,
          auto_save_data: { role: 'teacher' }
        });

        if (!sessionError) {
          setSessionId(session.id);
        }
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      showDialog(
        'error',
        'Gagal Memuat',
        'Terjadi kesalahan saat memuat pertanyaan. Silakan coba lagi nanti.',
        'Coba Lagi',
        () => window.location.reload()
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (value) => {
    const currentQuestion = questions[currentIndex];

    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: {
        question_id: currentQuestion.id,
        response_value: { value },
        time_spent_seconds: 0,
      }
    }));

    // Auto-advance to next question after short delay (if not last question)
    if (currentIndex < questions.length - 1 && !isAdvancingRef.current) {
      isAdvancingRef.current = true;

      // Check for milestones before advancing
      const newProgress = ((currentIndex + 2) / questions.length) * 100;
      const roundedProgress = Math.round(newProgress);
      if ([25, 50, 75].includes(roundedProgress) && !milestoneShownRef.current[roundedProgress]) {
        milestoneShownRef.current[roundedProgress] = true;
        showMilestoneCelebration(roundedProgress);
      }

      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        isAdvancingRef.current = false;
      }, 400);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const showMilestoneCelebration = (progress) => {
    const messages = {
      25: 'Awal yang bagus! 25% selesai',
      50: 'Setengah jalan! Terus lanjutkan!',
      75: 'Hampir selesai! Sedikit lagi!',
    };

    setCelebrationMessage(messages[progress]);
    setShowCelebration(true);

    setTimeout(() => {
      setShowCelebration(false);
    }, 2000);
  };

  const handleSubmit = async () => {
    // Check if all questions answered
    const unanswered = questions.filter(q => !responses[q.id]);

    if (unanswered.length > 0) {
      showDialog(
        'warning',
        'Pertanyaan Belum Lengkap',
        `Anda memiliki ${unanswered.length} pertanyaan yang belum dijawab. Yakin ingin mengirim penilaian?`,
        'Ya, Kirim',
        submitAssessment
      );
      return;
    }

    submitAssessment();
  };

  const submitAssessment = async () => {
    closeDialog();
    setSubmitting(true);

    try {
      // Submit all responses
      const responsesToSubmit = Object.values(responses).map(r => ({
        ...r,
        session_id: sessionId,
        user_id: profile?.id,
      }));

      const { error } = await assessmentService.submitResponses(responsesToSubmit);

      if (error) throw error;

      // Mark session as complete
      await assessmentService.completeSession(sessionId);

      // Clear saved data
      clearSavedData();

      // Show success dialog
      showDialog(
        'success',
        'Penilaian Berhasil',
        'Terima kasih! Penilaian Anda telah berhasil dikirim dan akan membantu meningkatkan kualitas kepemimpinan di sekolah.',
        'Kembali ke Dashboard',
        () => navigate('/dashboard')
      );
    } catch (error) {
      console.error('Error submitting assessment:', error);
      showDialog(
        'error',
        'Gagal Mengirim',
        'Terjadi kesalahan saat mengirim penilaian. Data Anda telah tersimpan dan Anda dapat mencoba lagi nanti.',
        'Tutup'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat penilaian...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(responses).length;
  const isLastQuestion = currentIndex === questions.length - 1;

  // Rating scale configuration
  const ratingScale = [
    { value: 1, label: 'Sangat Tidak Setuju', color: 'bg-red-500', textColor: 'text-red-600' },
    { value: 2, label: 'Tidak Setuju', color: 'bg-orange-500', textColor: 'text-orange-600' },
    { value: 3, label: 'Netral', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
    { value: 4, label: 'Setuju', color: 'bg-blue-500', textColor: 'text-blue-600' },
    { value: 5, label: 'Sangat Setuju', color: 'bg-green-500', textColor: 'text-green-600' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Dialog */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        onConfirm={dialog.onConfirm}
      />

      {/* Celebration Toast */}
      {showCelebration && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-bounce">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-full shadow-lg">
            <p className="font-semibold">{celebrationMessage}</p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Penilaian Kinerja Kepala Sekolah</h1>
              <p className="text-sm text-gray-600">Penilaian oleh Guru</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Simpan & Keluar
            </button>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Pertanyaan {currentIndex + 1} dari {questions.length}</span>
              <span>{Math.round(progress)}% Selesai</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Status indicators */}
          <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
            <div className="flex items-center gap-4">
              {isSaving && (
                <span className="flex items-center gap-1">
                  <span className="animate-pulse text-blue-500">●</span> Menyimpan...
                </span>
              )}
              {lastSaved && !isSaving && (
                <span className="text-green-600">● Tersimpan</span>
              )}
              {isSyncing && (
                <span className="text-purple-600">● Sinkronisasi...</span>
              )}
            </div>
            <span className="text-gray-400">
              {answeredCount} dari {questions.length} dijawab
            </span>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
              {currentQuestion?.category?.replace(/_/g, ' ').toUpperCase() || 'PERTANYAAN'}
            </span>
          </div>

          {/* Question Number & Text */}
          <div className="mb-8">
            <div className="text-4xl font-bold text-indigo-600 mb-3">
              {currentIndex + 1}.
            </div>
            <h2 className="text-lg text-gray-800 leading-relaxed">
              {currentQuestion?.question_text}
            </h2>
          </div>

          {/* Rating Options - Professional Style */}
          <div className="space-y-3 mb-6">
            {ratingScale.map((scale) => (
              <button
                key={scale.value}
                onClick={() => handleResponse(scale.value)}
                className={`
                  w-full p-4 rounded-xl border-2 text-left transition-all duration-200
                  flex items-center gap-4 group
                  ${responses[currentQuestion?.id]?.response_value?.value === scale.value
                    ? `${scale.color} border-transparent text-white shadow-lg`
                    : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-md'
                  }
                `}
              >
                {/* Number Badge */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0
                  ${responses[currentQuestion?.id]?.response_value?.value === scale.value
                    ? 'bg-white/20 text-white'
                    : `bg-gray-100 ${scale.textColor} group-hover:bg-gray-200`
                  }
                `}>
                  {scale.value}
                </div>

                {/* Label */}
                <span className={`
                  font-medium text-base
                  ${responses[currentQuestion?.id]?.response_value?.value === scale.value
                    ? 'text-white'
                    : 'text-gray-700'
                  }
                `}>
                  {scale.label}
                </span>

                {/* Check Icon */}
                {responses[currentQuestion?.id]?.response_value?.value === scale.value && (
                  <svg className="w-6 h-6 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-100">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`
                px-5 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2
                ${currentIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                }
              `}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Kembali
            </button>

            {isLastQuestion ? (
              <button
                onClick={handleSubmit}
                disabled={submitting || answeredCount === 0}
                className={`
                  px-6 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2
                  ${submitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : answeredCount === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg hover:scale-105'
                  }
                `}
              >
                {submitting ? 'Mengirim...' : 'Kirim Penilaian'}
                {!submitting && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ) : (
              <div className="text-sm text-indigo-600 font-medium bg-indigo-50 px-4 py-2 rounded-lg">
                Pilih jawaban untuk lanjut →
              </div>
            )}
          </div>
        </div>

        {/* Progress Navigator - Fixed at bottom */}
        <div className="bg-white rounded-2xl shadow-md p-4 sticky bottom-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500">Navigasi Pertanyaan</span>
            <span className="text-xs text-gray-400">
              Hijau = Sudah dijawab
            </span>
          </div>
          <div className="flex gap-1.5 overflow-x-auto py-1 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`
                  flex-shrink-0 w-8 h-8 rounded-lg text-xs font-medium transition-all
                  ${idx === currentIndex
                    ? 'bg-indigo-600 text-white shadow-md scale-110'
                    : responses[q.id]
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }
                `}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherAssessment;
